import React, { forwardRef } from "react";
import { bool, func, node, oneOf } from "prop-types";

import actionElements from "../constants/actionElements";
import componentSizes from "../constants/componentSizes";
import buttonActionThemes from "./buttonActionThemes";
import * as S from "./styled";

const ButtonAction = forwardRef(
  (
    {
      children,
      disabled,
      element,
      href,
      isLoading,
      onClick,
      size,
      theme,
      type,
      ...passThroughProps
    },
    ref
  ) => (
    <S.ButtonActionBase
      ref={ref}
      as={element}
      href={element === actionElements.a ? href : null}
      isDisabled={disabled || isLoading}
      isLoading={isLoading}
      onClick={disabled ? null : onClick}
      size={size}
      theme={theme}
      type={element === actionElements.button ? type : null}
      {...passThroughProps}
    >
      {S.TERTIARY_THEMES.includes(theme) && isLoading && (
        <S.TertiaryLoadingIcon />
      )}
      {children}
    </S.ButtonActionBase>
  )
);

ButtonAction.propTypes = {
  children: node.isRequired,
  /**
   * When this prop is true, Button Action will be disabled (non-interactive).
   *
   * When ButtonAction is a semantic HTML button element, the HTML `disabled`
   * attribute will be applied.
   */
  disabled: bool,
  /*
   * ButtonAction can be configured to render either an HTML anchor element or
   * and HTML button element.
   *
   *  By default, ButtonAction will render a button element.
   * Valid element types are referenced through the actionElements enum:
   * "@streeteasy/design-system/constants/actionElements".
   * */

  element: oneOf(Object.values(actionElements)),
  /**
   * When this prop is true, ButtonAction will show a loading state.
   *
   * This will also disable the ButtonAction. This might be useful if we
   * want to prevent user from interacting with form while we processing server request.
   */
  isLoading: bool,
  /**
   * When ButtonAction renders an anchor element, href is required
   * */
  href: props => {
    if (!props.href && props.element === actionElements.a) {
      return new Error("The href prop is required when ButtonAction is a link");
    }

    if (typeof props.href !== "string" && props.element === actionElements.a) {
      return new Error("href must be a string");
    }

    return null;
  },
  onClick: func,
  /**
   * Determines the size of ButtonAction. Values for the size prop are imported
   * from the componentSizes module: "@streeteasy/design-system/constants/componentSizes".
   * */
  size: oneOf(Object.values(componentSizes)),
  type: oneOf(["button", "submit", "reset"]),
  /**
   * Determines background and text color of ButtonAction.
   * Values for the theme prop are imported from the buttonActionThemes module.
   * */
  theme: oneOf(Object.values(buttonActionThemes))
};

ButtonAction.defaultProps = {
  disabled: false,
  element: actionElements.button,
  isLoading: false,
  href: null,
  onClick: null,
  size: componentSizes.MD,
  theme: buttonActionThemes.PRIMARY,
  type: "submit"
};

export default ButtonAction;
